import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Copy, Check } from 'lucide-react';
import BackButton from '../../components/BackButton';
import toast from 'react-hot-toast';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { articles } from '../../data/articles';
import { formatDate } from '../../utils/dateFormat';
import PageTitle from '../../components/PageTitle';

export default function DashboardArticleDetail() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { t, i18n } = useTranslation('dashboard');
    const currentLang = i18n.language.startsWith('zh') ? 'zh' : 'en';

    const article = articles.find((a) => a.id === id);

    const [viewLang, setViewLang] = useState<'en' | 'zh'>(currentLang);
    const [copiedRaw, setCopiedRaw] = useState(false);
    const [copiedRendered, setCopiedRendered] = useState(false);
    const [copiedMain, setCopiedMain] = useState(false);

    if (!article) {
        return (
            <div className="p-8 text-center text-muted-foreground">
                {t('articles.no_articles')}
            </div>
        );
    }

    const handleCopy = async (text: string, type: 'raw' | 'rendered' | 'main') => {
        try {
            await navigator.clipboard.writeText(text);
            toast.success(t('articles.copy_success'));
            if (type === 'raw') setCopiedRaw(true);
            if (type === 'rendered') setCopiedRendered(true);
            if (type === 'main') setCopiedMain(true);

            setTimeout(() => {
                if (type === 'raw') setCopiedRaw(false);
                if (type === 'rendered') setCopiedRendered(false);
                if (type === 'main') setCopiedMain(false);
            }, 2000);
        } catch (err) {
            toast.error(t('articles.copy_failed'));
        }
    };

    const contentToDisplay = article.content[viewLang];

    return (
        <div className="p-8 h-full flex flex-col overflow-hidden">
            <PageTitle dynamicTitle={`${article.title[currentLang]} | Articles`} namespace="dashboard" />
            <div className="mb-6 flex items-center justify-between shrink-0">
                <div className="flex items-center gap-4">
                    <BackButton onClick={() => navigate('/dashboard/articles')} />
                    <div>
                        <h1 className="text-2xl font-semibold text-foreground tracking-tight">
                            {article.title[currentLang]}
                        </h1>
                        <p className="text-sm text-muted-foreground">
                            {formatDate(article.date)}
                        </p>
                    </div>
                </div>
            </div>

            <div className="mb-4 flex items-center justify-between shrink-0">
                <div className="flex items-center gap-3 text-sm">
                    <span className="text-muted-foreground mr-1">{t('article.language_label')}</span>
                    <div className="flex bg-muted p-1 rounded-md">
                        <button
                            onClick={() => setViewLang('en')}
                            className={`px-3 py-1 text-xs rounded-sm transition-colors ${viewLang === 'en' ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                        >
                            {t('article.language_english')}
                        </button>
                        <button
                            onClick={() => setViewLang('zh')}
                            className={`px-3 py-1 text-xs rounded-sm transition-colors ${viewLang === 'zh' ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                        >
                            中文
                        </button>
                    </div>
                </div>
                <button
                    onClick={() => handleCopy(contentToDisplay, 'main')}
                    className="flex items-center gap-2 px-3 py-1.5 bg-primary hover:bg-primary/90 text-primary-foreground rounded-md text-sm font-medium transition-colors"
                >
                    {copiedMain ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                    {t('article.copy_markdown')}
                </button>
            </div>

            <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-4 min-h-0">
                {/* Left Column: Raw Markdown */}
                <div className="bg-card shadow-sm rounded-xl border border-border flex flex-col min-h-0 overflow-hidden">
                    <div className="px-4 py-3 border-b border-border flex justify-between items-center bg-muted/10 shrink-0">
                        <span className="font-medium text-sm text-foreground">{t('article.markdown_source')}</span>
                        <button
                            onClick={() => handleCopy(contentToDisplay, 'raw')}
                            className="p-1 hover:bg-muted text-muted-foreground hover:text-foreground rounded-sm transition-colors"
                            title={t('article.copy')}
                        >
                            {copiedRaw ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
                        </button>
                    </div>
                    <div className="p-4 bg-background font-mono text-sm text-foreground whitespace-pre-wrap overflow-y-auto flex-1 outline-none">
                        {contentToDisplay}
                    </div>
                </div>

                {/* Right Column: Rendered Content */}
                <div className="bg-card shadow-sm rounded-xl border border-border flex flex-col min-h-0 overflow-hidden">
                    <div className="px-4 py-3 border-b border-border flex justify-between items-center bg-muted/10 shrink-0">
                        <span className="font-medium text-sm text-foreground">{t('article.live_preview')}</span>
                        <button
                            onClick={() => {
                                const previewElement = document.getElementById('article-live-preview');
                                const textToCopy = previewElement ? previewElement.innerText : contentToDisplay;
                                handleCopy(textToCopy, 'rendered');
                            }}
                            className="p-1 hover:bg-muted text-muted-foreground hover:text-foreground rounded-sm transition-colors"
                            title={t('article.copy_text')}
                        >
                            {copiedRendered ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
                        </button>
                    </div>
                    <div className="p-6 overflow-y-auto flex-1 bg-background">
                        <div id="article-live-preview" className="prose max-w-none">
                            <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                {contentToDisplay}
                            </ReactMarkdown>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
